<template>
  <div>
    <section v-if="hasDoneTrainings">
      <patient-dinamics
        ref="dinamic"
        :course="course"
        :doneTrainings="doneTrainings"
        :trainingsInfo="trainingsInfo"
      ></patient-dinamics>
    </section>
    <section>
      <PatientDiary ref="diary"></PatientDiary>
    </section>
    <section>
      <div v-if="loading">
        <base-spinner></base-spinner>
      </div>
      <div v-else-if="hasTrainings || isRehabCourse" class="card mt-3 mb-2">
        <div class="card-header card-header-light d-flex justify-content-between">
          <div class="d-flex">
            <h4 class="card-header__title mb-1 mr-4">
              {{ $t('src/components/training/trainingmanage.3872') }}
            </h4>

            <button
              v-if="hasRightsToEdit && !isShowGenerator"
              type="button"
              class="btn btn-link btn-with-icon"
              @click="regenerateTrainings"
            >
              <svg-icon class="svg-icon" :name="'edit-trainings'" :height="20" :width="25"></svg-icon>
              {{
                hasTrainings
                  ? $t('src/components/training/trainingmanage.changeSessions')
                  : $t('src/components/training/trainingmanage.generateSessions')
              }}
            </button>
          </div>

          <div class="card-header__month-pagination d-flex flex-row">
            <div v-if="showMonthSwitch" class="d-flex flex-row">
              <svg-icon
                @click:icon="previousMonth"
                :name="'arrow-right'"
                class="svg-icon-small mr-1 mt-2"
                style="transform: scaleX(-1)"
                :class="!isDisplayPrevious ? 'svg-icon-hide' : ''"
              >
              </svg-icon>

              <span class="month-name">
                {{ monthName }}
              </span>

              <svg-icon
                @click:icon="nextMonth"
                :name="'arrow-right'"
                class="svg-icon-small ml-1 mt-2"
                :class="!isDisplayNext ? 'svg-icon-hide' : ''"
              >
              </svg-icon>
            </div>

            <div v-if="hasTrainings" class="mr-3 ml-3" style="border-left: 1px solid #D9D9DD;"></div>

            <div v-if="hasTrainings" class="btn-group">
              <button
                type="button"
                class="btn btn-outline-none btn-with-icon"
                :class="trainingView === 'line' ? 'btn-secondary-light' : 'btn-outline-secondary'"
                @click="setView('line')"
                :title="$t('src/components/training/trainingmanage.1')"
              >
                <svg-icon :name="'menu_burger'"></svg-icon>
              </button>
              <button
                type="button"
                class="btn btn-outline-none btn-with-icon"
                :class="trainingView === 'table' ? 'btn-secondary-light' : 'btn-outline-secondary'"
                @click="setView('table')"
                :title="$t('src/components/training/trainingmanage.2')"
              >
                <svg-icon @click:icon="setView('table')" :name="'menu_grid'"></svg-icon>
              </button>
            </div>
          </div>
        </div>
        <div class="card-body card-body-wide" v-if="hasTrainings">
          <div class="table-responsive">
            <component
              :is="trainingViewComponent"
              :trainings="trainings"
              :has-rights-to-edit="hasRightsToEdit"
              :saving="saving"
              :month="month"
              :year="year"
              @create="createTrainingByUser"
              @remove="confirmRemoveTraining"
            ></component>
          </div>
        </div>
        <div v-else class="text-center mb-3">
          <svg-icon
            class="svg-icon-without-hover svg-icon-big"
            :name="'empty-list'"
            :height="90"
            :width:="90"
          ></svg-icon>
          <div class="text-light">{{ $t('general.emptySessionsList') }}</div>
        </div>
      </div>
      <div class="card mt-3 mb-2" v-if="isShowGenerator" ref="trainingGenerator">
        <div class="card-header card-header-light d-flex justify-content-between">
          <h4 class="card-header__title">{{ $t('src/components/training/trainingmanage.1607') }}</h4>
        </div>
        <div class="card-body">
          <training-generator
            :course="course"
            :last-training-date="lastTrainingDate"
            @generate="saveTrainings"
          ></training-generator>
        </div>
      </div>
      <base-modal
        v-if="hasRightsToEdit"
        :is-show="isShowRegenerateModal"
        @cancel="closeRegenerateModal"
        @close="closeRegenerateModal"
        @confirm="startGenerate"
      >
        <template #header>{{ $t('src/components/training/trainingmanage.5739') }}</template>
        <template #body>
          <div class="pt-3 pb-3">
            <p>
              {{ $t('src/components/training/trainingmanage.1662')
              }}<strong>{{ firstDeletedTrainingDate }}</strong
              >.
            </p>
            <p>{{ $t('src/components/training/trainingmanage.7656') }}</p>
          </div>
        </template>
      </base-modal>
      <base-modal
        v-if="hasRightsToEdit"
        :is-show="isShowRemoveModal"
        @cancel="closeRemoveModal"
        @close="closeRemoveModal"
        @confirm="removeTrainingByUser"
      >
        <template #header>{{ $t('src/components/training/trainingmanage.9727') }}</template>
        <template #body>
          <div class="pt-3 pb-3">
            <p>{{ $t('src/components/training/trainingmanage.6944') }}</p>
          </div>
        </template>
      </base-modal>
    </section>
  </div>
</template>

<script>
import http from '@/modules/http'
import { status } from '@/main.config.js'
import { stringify } from 'qs'
import { userRights } from '@/mixins/user-rights.js'
import { courseTypes, DATE_FORMAT } from '@/main.config'
import { trainingDate } from '@/mixins/training-date.js'
import { nowMoment } from '@/mixins/now-moment'
import { nameMonths } from '@/mixins/name-months.js'
import { systemUnavailable } from '@/mixins/system-unavailable.js'

export default {
  name: 'TrainingManage',
  components: {
    TrainingTable: () => import('@/components/Training/TrainingTable.vue'),
    TrainingGenerator: () => import('@/components/Training/TrainingGenerator.vue'),
    TrainingList: () => import('@/components/Training/TrainingList.vue'),
    BaseSpinner: () => import('@/base/BaseSpinner.vue'),
    BaseModal: () => import('@/base/BaseModal.vue'),
    SvgIcon: () => import('@/components/SvgIcon.vue'),
    PatientDinamics: () => import('@/components/PatientDinamics.vue'),
    PatientDiary: () => import('@/components/PatientDiary.vue')
  },
  mixins: [userRights, trainingDate, nowMoment, nameMonths, systemUnavailable],
  props: {
    course: {
      type: Object,
      required: true
    },
    updateSessionSettings: {
      type: Boolean,
      required: true
    }
  },
  data: () => ({
    trainings: [],
    isShowGenerator: false,
    isShowRemoveModal: false,
    loading: true,
    saving: false,
    isShowRegenerateModal: false,
    trainingView: null,
    removedId: null,
    month: null,
    year: null,
    isOnly6mwt: false
  }),
  computed: {
    hasRightsToEdit() {
      return this.isCourseCreator && this.isRehabCourse && !this.isClosedCourse
    },
    isClosedCourse() {
      if (!this.course) {
        return true
      }
      return this.course.status === status.closed || this.course.status === status.closedWrong
    },
    isRehabCourse() {
      return this.course.course_type === courseTypes.rehab || this.course.course_type === null
    },
    isCourseCreateToday() {
      const createdAt = this.moment(this.course.createdAt, DATE_FORMAT.serverFull)
      return createdAt.isSame(this.nowMoment, 'day')
    },
    courseId() {
      return this.course.id
    },
    trainingViewComponent() {
      if (this.trainingView === 'line') {
        return 'TrainingList'
      } else {
        return 'TrainingTable'
      }
    },
    hasTrainings() {
      return this.trainings && this.trainingsCount > 0
    },
    hasDoneTrainings() {
      return this.doneTrainings && this.doneTrainings.length > 0
    },
    trainingsCount() {
      return this.trainings.length
    },
    trainingsInfo() {
      if (!this.hasTrainings) {
        return null
      }
      return {
        all: this.trainingsCount,
        done: this.doneTrainings.length,
        doneTrainingsForProtocol: this.doneTrainings,
        lastDate: this.lastTrainingDate,
        active: this.activeTrainings,
        missed: this.missedTrainings.length
      }
    },
    lastTrainingDate() {
      return this.getTrainingMomentStartDate(this.trainings[this.trainingsCount - 1])
    },
    baseTrainingSettings() {
      return {
        status: status.plan,
        course_id: this.course.id,
        heart_rate_min: this.course.opt_watch_hr ? this.course.course_hr_min : null,
        heart_rate_max: this.course.opt_watch_hr ? this.course.course_hr_max : null,
        duration: this.course.opt_show_duration ? this.durationInMinutes : null
      }
    },
    trainingsForReport() {
      if (this.isOnly6mwt) {
        return false
      } else return true
    },
    doneTrainings() {
      if (!this.hasTrainings) {
        return []
      }
      let doneTrain = this.trainings.filter(training => training.status === status.done)
      return doneTrain
    },
    missedTrainings() {
      if (!this.hasTrainings) {
        return []
      }
      let missedTrain = this.trainings.filter(training => training.status === status.missed)
      return missedTrain
    },
    wt6mTrainings() {
      if (!this.hasTrainings) {
        return []
      }
      let a = this.trainings.filter(
        training => training.status === status.done && training.training_type === 1
      )
      return a
    },
    standardTrainings() {
      if (!this.hasTrainings) {
        return []
      }
      let a = this.trainings.filter(
        training => training.status === status.done && training.training_type === 0
      )
      return a
    },
    activeTrainings() {
      if (!this.hasTrainings) {
        return []
      }
      return this.trainings.filter(training => training.status === status.active)
    },
    remainingTrainings() {
      if (!this.hasTrainings) {
        return []
      }
      return this.trainings.filter(training => {
        if (training.status !== status.plan) {
          return true
        }
        const trainingDate = this.getTrainingMomentStartDate(training)
        if (this.isCourseCreateToday) {
          return trainingDate.isBefore(this.nowMoment, 'day')
        } else {
          return trainingDate.isSameOrBefore(this.nowMoment, 'day')
        }
      })
    },
    firstDeletedTrainingDate() {
      if (!this.hasTrainings) {
        return null
      }
      const training = this.trainings.find(training => {
        if (training.status !== status.plan) {
          return false
        }
        const trainingDate = this.getTrainingMomentStartDate(training)
        if (this.isCourseCreateToday) {
          return trainingDate.isSameOrAfter(this.nowMoment, 'day')
        } else {
          return trainingDate.isAfter(this.nowMoment, 'day')
        }
      })
      return training ? this.getTrainingFormattedStartDate(training) : null
    },
    durationInMinutes() {
      return this.getDurationInMinutes(this.course.training_duration, this.course.training_duration_unit)
    },
    monthName() {
      return this.getNominativeMonths(this.month) + ' ' + this.year
    },
    firstMonth() {
      return this.trainings[0].date_start
        ? this.moment(this.trainings[0].date_start).month()
        : this.moment(this.trainings[0].date_plan).month()
    },
    firstYear() {
      return this.trainings[0].date_start
        ? this.moment(this.trainings[0].date_start).year()
        : this.moment(this.trainings[0].date_plan).year()
    },
    lastMonth() {
      return this.trainings[this.trainings.length - 1].date_start
        ? this.moment(this.trainings[this.trainings.length - 1].date_start).month()
        : this.moment(this.trainings[this.trainings.length - 1].date_plan).month()
    },
    lastYear() {
      return this.trainings[this.trainings.length - 1].date_start
        ? this.moment(this.trainings[this.trainings.length - 1].date_start).year()
        : this.moment(this.trainings[this.trainings.length - 1].date_plan).year()
    },
    closeCourseMonth() {
      return this.moment(this.course.close_date).month()
    },
    showMonthSwitch() {
      if (this.trainingView === 'table' && this.trainings.length > 0) return true
      else return false
    },
    isDisplayPrevious() {
      if (this.firstYear < this.year) return true
      else {
        if (this.firstMonth < this.month) return true
        else return false
      }
    },
    isDisplayNext() {
      if (!this.course.close_date) return true
      if (this.closeCourseMonth > this.month) return true
      if (this.lastYear > this.year) return true
      else {
        if (this.lastMonth > this.month) return true
        else return false
      }
    }
  },
  watch: {
    trainingsInfo() {
      this.$emit('change:trainings', this.trainingsInfo)
    },
    course() {
      if (this.updateSessionSettings) {
        this.getTrainings()
      }
    }
  },
  created() {
    this.setStorageTrainingView()
    this.getTrainings()
  },

  methods: {
    regenerateTrainings() {
      if (Number(localStorage.getItem('activeHospital'))) {
        if (this.firstDeletedTrainingDate) {
          this.isShowRegenerateModal = true
        } else {
          this.startGenerate()
        }
      } else {
        this.$emit('system:unavailable')
      }
    },
    startGenerate() {
      this.isShowGenerator = true
      this.isShowRegenerateModal = false
      if (this.firstDeletedTrainingDate) {
        this.deletePlannedTrainings()
      }
      this.scrollIntoTrainingGenerator()
    },
    deletePlannedTrainings() {
      const dateCondition = this.isCourseCreateToday ? '$gte' : '$gt' //если курс создан сегодня то удаляем, включая текущую дату
      this.trainings = this.remainingTrainings

      http
        .delete('/trainings/', {
          params: {
            course_id: this.courseId,
            date_plan: {
              [dateCondition]: this.nowFormatServer
            }
          },
          paramsSerializer: params => stringify(params, { arrayFormat: 'brackets' })
        })
        .catch(error => {
          console.log(error)
        })
    },
    scrollIntoTrainingGenerator() {
      setTimeout(() => {
        this.$refs.trainingGenerator.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 200)
    },
    saveTrainings(trainings, isUpdateCourseCloseDate) {
      this.isShowGenerator = false
      this.loading = true
      const lastIndex = trainings.length - 1

      http
        .post('/trainings', trainings)
        .then(response => response.data)
        .then(trainings => {
          this.trainings = trainings.sort(this.sortTrainingsByDate)
          this.getMonth()
        })
        .catch(error => {
          if (error.response.data.message === 'The system has expired') {
            this.$emit('system:unavailable')
          }
          console.error(error)
        })
        .finally(() => {
          if (isUpdateCourseCloseDate) {
            this.changeCourse(trainings[lastIndex].date_plan)
          }
          this.loading = false
        })
    },
    confirmRemoveTraining(id) {
      this.isShowRemoveModal = true
      this.removedId = id
    },
    removeTrainingByUser() {
      http
        .delete('/trainings/' + this.removedId)
        .then(response => response.data.id)
        .then(id => {
          const index = this.trainings.findIndex(training => training.id === id)
          this.trainings.splice(index, 1)
          this.removedId = null
        })
        .catch(error => {
          console.log(error)
        })
        .finally(() => {
          this.closeRemoveModal()
        })
    },
    createTrainingByUser(date) {
      const training = Object.assign(
        {
          date_plan: date,
          date_start: date
        },
        this.baseTrainingSettings
      )
      this.createTraining(training).then(newTraining => {
        this.trainings.push(newTraining)
        this.trainings.sort(this.sortTrainingsByDate)
      })
    },
    createTraining(training) {
      this.saving = true
      return new Promise(resolve => {
        http
          .post('/trainings', training)
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            if (error.response.data.message === 'The system has expired') {
              this.$emit('system:unavailable')
            }
            console.log(error)
          })
          .finally(() => {
            this.saving = false
          })
      })
    },
    changeCourse(lastTrainingDate) {
      this.$emit('save:course', {
        close_date: lastTrainingDate
      })
    },
    getTrainings() {
      this.loading = true
      http
        .get('/trainings', {
          params: {
            course_id: this.courseId,
            $sort: {
              date_start: 1
            },
            $limit: -1
          },
          paramsSerializer: params => stringify(params, { arrayFormat: 'brackets' })
        })
        .then(response => response.data)
        .then(data => {
          this.trainings = data.sort(this.sortTrainingsByDate)
          if (data.length > 0) {
            this.getMonth()
          }
        })
        .catch(error => console.log(error))
        .finally(() => (this.loading = false))
    },
    getMonth() {
      let storageName = `month${this.courseId}`
      let storageMonthAndYearArray = window.sessionStorage.getItem(storageName)?.split('_')

      if (!storageMonthAndYearArray || (!+storageMonthAndYearArray[0] || !+storageMonthAndYearArray[1])) {
        let currentMonth = this.moment().month()
        let currentYear = this.moment().year()
        let lastMonth = this.trainings[this.trainings.length - 1].date_start
          ? this.moment(this.trainings[this.trainings.length - 1].date_start).month()
          : this.moment(this.trainings[this.trainings.length - 1].date_plan).month()
        let lastYear = this.trainings[this.trainings.length - 1].date_start
          ? this.moment(this.trainings[this.trainings.length - 1].date_start).year()
          : this.moment(this.trainings[this.trainings.length - 1].date_plan).year()

        this.month = this.checkLastMonthAndYearLessThanCurrentMonthAndYear(
          lastMonth,
          lastYear,
          currentMonth,
          currentYear
        )
          ? this.trainings.length > 0
            ? lastMonth
            : currentMonth
          : currentMonth
        this.year =
          lastYear < currentYear ? (this.trainings.length > 0 ? lastYear : currentYear) : currentYear
        window.sessionStorage.setItem(storageName, `${this.month}_${this.year}`)
      } else {
        this.month = +storageMonthAndYearArray[0]
        this.year = +storageMonthAndYearArray[1]
      }
    },
    checkLastMonthAndYearLessThanCurrentMonthAndYear(lastMonth, lastYear, currentMonth, currentYear) {
      if (lastYear < currentYear) {
        return true
      } else {
        if (lastMonth < currentMonth) return true
        else return false
      }
    },
    sortTrainingsByDate(a, b) {
      let aDate = this.moment(a.date_plan)
      let bDate = this.moment(b.date_plan)
      if (a.date_start) {
        aDate = this.moment(a.date_start)
      }
      if (b.date_start) {
        bDate = this.moment(b.date_start)
      }
      return aDate.isSameOrBefore(bDate) ? -1 : 1
    },
    closeRegenerateModal() {
      this.isShowRegenerateModal = false
    },
    closeRemoveModal() {
      this.isShowRemoveModal = false
    },
    setView(view) {
      this.trainingView = view
      window.sessionStorage.setItem('trainingView', view)
    },
    setStorageTrainingView() {
      let storageTrainingView = window.sessionStorage.getItem('trainingView')
      if (!storageTrainingView) {
        if (this.isRehabCourse)
          window.sessionStorage.setItem('trainingView', 'table')
        else
          window.sessionStorage.setItem('trainingView', 'line')
      }
      this.trainingView = window.sessionStorage.getItem('trainingView')
    },
    isDisplay(month) {
      if (month !== this.month) return false
      else return true
    },
    nextMonth() {
      if (this.month == 11) {
        this.month = 0
        this.year = this.year + 1
      } else {
        this.month = this.month + 1
      }
      window.sessionStorage.setItem(`month${this.courseId}`, `${this.month}_${this.year}`)
    },
    previousMonth() {
      if (this.firstMonth !== this.month || this.firstYear !== this.year) {
        if (this.month == 0) {
          this.month = 11
          this.year = this.year - 1
        } else {
          this.month = this.month - 1
        }
        window.sessionStorage.setItem(`month${this.courseId}`, `${this.month}_${this.year}`)
      }
    }
  }
}
</script>
